import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { rem, stripUnit } from 'polished'
import { SVGWrapper } from '../Image/Image.styles'

export const buttonStyles = ({
  color,
  gradient,
  theme,
  rounded,
  shadow,
  variant,
  outlined,
  ghost,
  leftIcon,
  rightIcon,
  visibility,
}) => {
  let borderRadius
  let colorAdjustment = 'dark'
  const gradientColor =
    (gradient &&
      theme?.components?.Button?.variants.gradients?.[color]?.color) ||
    'initial'
  // This gives us access to the first gradient color in the theme, which we can
  // use to set the fill of an svg icon to match the button color while still following the gradient.
  const selectedGradient = theme?.gradients?.[color]
  const gradientHexCodes =
    selectedGradient?.match(/#(?:[0-9a-fA-F]{3}){1,2}/g) || null

  if (rounded === 'min') {
    borderRadius = theme?.radius
  } else if (rounded === 'max') {
    borderRadius = '2em'
  } else {
    borderRadius = 'none'
  }

  if (!theme?.colors?.[color]?.dark) {
    colorAdjustment = 'light'
  }

  return css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${theme?.spacing?.xxs} ${theme?.spacing?.sm};
    font: inherit;
    color: ${theme?.colors?.[color]?.base?.readable};
    background-color: ${theme?.colors?.[color]?.base?.hex};
    ${theme?.components?.Button?.variants?.[color]?.color
      ? `color: ${theme?.components?.Button.variants?.[color]?.color};`
      : `color: ${theme?.colors?.[color]?.base?.readable};`}
    border: 0;
    border-radius: ${borderRadius};
    cursor: pointer;
    transition: all 0.3s;
    -webkit-appearance: button;

    /**
        *TODO: Are we sure we want/need this attribute?
        * Testing it in storybook through multi-step forms
        * showed that the button and the spans within it were
        * all firing click events fine, but further testing
        * should be done.
        */

    > * {
      pointer-events: none;
    }

    svg {
      ${theme?.components?.Button?.variants?.[color]?.color &&
      `fill: ${theme?.components?.Button?.variants?.[color]?.color};`}
      margin: 0;
    }

    span {
      ${theme?.components?.Button?.variants?.[color]?.color &&
      `color: ${theme?.components?.Button?.variants?.[color]?.color};`}
    }

    ${SVGWrapper} svg, svg {
      flex-shrink: 0;
      height: ${theme?.spacing?.sm};
      width: ${theme?.spacing?.sm};
    }

    .gatsby-image-wrapper {
      height: ${theme?.spacing?.sm};
      width: ${theme?.spacing?.sm};
      margin-bottom: 0 !important;
    }

    .button-text {
      ${leftIcon && `margin-left: ${theme?.spacing?.xxs}`};
      ${rightIcon && `margin-right: ${theme?.spacing?.xxs}`};
    }

    &:hover {
      ${theme?.components?.Button?.variants?.[color]?.hoverColor
        ? `color: ${theme?.components?.Button?.variants?.[color]?.hoverColor};`
        : `color: ${theme?.colors?.[color]?.[colorAdjustment].readable};`}
      ${theme?.components?.Button?.variants?.[color]?.hoverBg
        ? `background-color: ${theme?.components?.Button?.variants?.[color]?.hoverBg};`
        : `background-color: ${theme?.colors?.[color]?.[colorAdjustment].hex};`}
          svg {
        ${theme?.components?.Button?.variants?.[color]?.hoverColor &&
        `fill: ${theme?.components?.Button?.variants?.[color]?.hoverColor};`}
      }

      span {
        ${theme?.components?.Button?.variants?.[color]?.hoverColor &&
        `color: ${theme?.components?.Button.variants?.[color]?.hoverColor};`}
      }
    }

    &:active {
      ${theme?.components?.Button?.variants?.[color]?.activeColor
        ? `color: ${theme?.components?.Button?.variants?.[color]?.activeColor};`
        : `color: ${theme?.colors?.[color]?.[`${colorAdjustment}er`].readable}`}
      ${theme?.components?.Button?.variants?.[color]?.activeBg
        ? `background-color: ${theme?.components?.Button?.variants?.[color]?.activeBg}`
        : `background-color: ${
            theme?.colors?.[color]?.[`${colorAdjustment}er`].hex
          }`}
          svg {
        ${theme?.components?.Button?.variants?.[color]?.activeColor &&
        `fill: ${theme?.components?.Button?.variants?.[color]?.activeColor};`}
      }

      span {
        ${theme?.components?.Button?.variants?.[color]?.activeColor &&
        `color: ${theme?.components?.Button?.variants?.[color]?.activeColor};`}
      }
    }

    /* TODO: determine design system specifications for this state */

    &:disabled {
      background-color: ${theme?.colors?.light?.darkest?.hex};
      color: ${theme?.colors?.dark?.lightest?.hex};
    }

    ${shadow && `box-shadow: ${theme?.shadow}`};

    ${variant === 'small' &&
    `
      padding: ${theme?.spacing?.min} ${theme?.spacing?.xxs};

      ${SVGWrapper} svg, svg {
        height: ${theme?.spacing?.xs};
        width: ${theme?.spacing?.xs} !important;
      }

      .gatsby-image-wrapper {
        height: ${theme?.spacing?.xs};
        width: ${theme?.spacing?.xs};
        margin-bottom: 0 !important;
      }
      `};

    ${ghost &&
    `
      color: ${theme?.colors?.[color]?.base?.hex};
      box-shadow: none;
      background-color: transparent;

      ${SVGWrapper} svg, svg {
        ${
          theme?.components?.Button?.variants?.[color]?.color
            ? `fill: ${theme?.components?.Button?.variants?.[color]?.color};`
            : `fill: ${theme?.colors?.[color]?.base?.hex}`
        }
      }

      &:hover ${SVGWrapper} svg, &:hover svg {
        ${
          theme?.components?.Button?.variants?.[color]?.hoverColor
            ? `fill: ${theme?.components?.Button?.variants?.[color]?.hoverColor};`
            : `fill: ${theme?.colors?.[color]?.base?.readable}`
        }
      }
      `}

    ${variant === 'feature' &&
    `
      padding-top: ${theme?.spacing?.xs};
      padding-bottom: ${theme?.spacing?.xs};

      ${SVGWrapper} svg, svg {
        height: ${theme?.spacing?.md};
        width: ${theme?.spacing?.md} !important;
      }

      .gatsby-image-wrapper {
        height: ${theme?.spacing?.md};
        width: ${theme?.spacing?.md};
        margin-bottom: 0 !important;
      }

      .button-text {
        margin-left: ${leftIcon ? theme?.spacing?.xxs : ''};
        margin-right: ${rightIcon ? theme?.spacing?.xxs : ''};
      }
      `}

      ${outlined &&
    !gradient &&
    `
      background-color: transparent;
      border: 2px solid ${theme?.colors?.[color]?.base?.hex};
      color: ${theme?.colors?.[color]?.base?.hex};

      svg {
        fill: ${theme?.colors?.[color]?.base?.hex};
      }

      span {
        color: ${theme?.colors?.[color]?.base?.hex};
      }

      &:hover {
        background-color: transparent;
        border: 2px solid ${theme?.colors?.[color]?.base?.hex};
        color: ${theme?.colors?.[color]?.base?.hex};

        span {
          color: ${theme?.colors?.[color]?.base?.hex};
        }

        svg {
          fill: ${theme?.colors?.[color]?.base?.hex};
        }
      }

      &:active {
        background-color: transparent;
        color: ${theme?.colors?.[color]?.[`${colorAdjustment}er`].hex};
        border-color: ${theme?.colors?.[color]?.[`${colorAdjustment}er`].hex};
      }
    `};

    ${gradient &&
    `
      background: ${theme?.gradients?.[color]};
      color: ${gradientColor} !important;

      span {
        color: ${gradientColor} !important;
      }

      svg {
        fill: ${gradientColor};
      }

      &:hover, &:hover span {
        color: ${gradientColor};
      }

      &:hover svg {
        fill: ${gradientColor};
      }
    `}

    ${gradient &&
    outlined &&
    `
    box-shadow: none !important;
      background-color: transparent;
      border: double 2px transparent;
      border-radius: ${borderRadius};
      background-image: linear-gradient(white, white), ${
        theme?.gradients?.[color]
      };
      background-origin: border-box;
      -webkit-background-clip: padding-box, border-box;

      // This is needed for outline gradient buttons in modules
      .button-text, .button-text span {
        background: ${theme?.gradients?.[color]} !important;
        color: transparent !important;
        -webkit-background-clip: text !important;
      }

      svg {
        fill: ${gradientHexCodes?.[0] || 'initial'};
      }

      &:hover {
        .button-text {
          color: transparent;
        }

        svg {
          fill: ${gradientHexCodes?.[0] || 'initial'};
        }

        span {
          color: transparent;
        }
      }
    `}

      ${variant === 'mega' &&
    css`
      padding: ${theme?.spacing?.sm} ${theme?.spacing?.max};
      justify-self: stretch;
      flex-direction: column;

      .button-text {
        margin-top: ${leftIcon && theme?.spacing?.sm};
        margin-bottom: ${rightIcon && theme?.spacing?.sm};
        margin-left: 0;
        margin-right: 0;
      }

      ${SVGWrapper} svg, svg {
        margin: 0 auto;
        height: 16px;
        width: 16px !important;
      }

      .gatsby-image-wrapper {
        height: ${theme?.spacing?.xl};
        width: ${theme?.spacing?.xl};
        margin-bottom: 0 !important;
      }
    `}

      ${variant === 'hero' &&
    `
      padding-top: ${theme?.spacing?.sm};
      padding-bottom: ${theme?.spacing?.sm};
      padding-left: ${
        leftIcon
          ? rem(stripUnit(theme?.spacing?.xl) - stripUnit(theme?.spacing?.min))
          : theme?.spacing?.xl
      };
      padding-right: ${
        rightIcon
          ? rem(stripUnit(theme?.spacing?.xl) - stripUnit(theme?.spacing?.min))
          : theme?.spacing?.xl
      };

      ${SVGWrapper} svg, svg {
        height: ${theme?.spacing?.lg};
        width: ${theme?.spacing?.lg} !important;
      }

      .gatsby-image-wrapper {
        height: ${theme?.spacing?.lg};
        width: ${theme?.spacing?.lg};
        margin-bottom: 0 !important;
      }

      .button-text {
        margin-left: ${leftIcon ? theme?.spacing?.xxs : ''};
        margin-right: ${rightIcon ? theme?.spacing?.xxs : ''};
      }
    `}

    ${visibility === 'mobile only' &&
    css`
      @media (min-width: ${theme?.breakpoints.sm}) {
        display: none;
      }
    `}

    ${visibility === 'desktop only' &&
    css`
      @media (max-width: ${theme?.breakpoints?.sm}) {
        display: none;
      }
    `}
  `
}

export const Button = styled.button(buttonStyles)
